import { UnifiedIcon } from '@/components/UnifiedIcon';

export type StepperStepState = 'complete' | 'active' | 'upcoming';

export interface StepperStep {
  id: string;
  label: string;
  state: StepperStepState;
}

export interface StepperProps {
  steps: StepperStep[];
  'data-testid'?: string;
}

function StepCircle({ step, index }: { step: StepperStep; index: number }) {
  if (step.state === 'complete') {
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-success text-success-on">
        <UnifiedIcon name="Check" size={16} className="h-4 w-4" />
      </span>
    );
  }

  if (step.state === 'active') {
    return (
      <span
        className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[var(--kc-step-active)] text-sm font-semibold text-[var(--kc-step-active)] shadow-glow"
        aria-current="step"
      >
        {index + 1}
      </span>
    );
  }

  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-default text-sm font-medium text-muted">
      {index + 1}
    </span>
  );
}

/**
 * Horizontal builder step indicator (Identity → Intelligence → …).
 */
export function Stepper({ steps, 'data-testid': dataTestId }: StepperProps) {
  return (
    <nav
      data-testid={dataTestId ?? 'design-system-stepper'}
      aria-label="Progress"
      className="flex w-full items-center gap-2"
    >
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const lineComplete = step.state === 'complete';

        return (
          <div key={step.id} className="flex min-w-0 flex-1 items-center gap-2 last:flex-none">
            <div className="flex min-w-0 flex-col items-center gap-1">
              <StepCircle step={step} index={index} />
              <span
                className={`hidden truncate text-xs sm:block ${
                  step.state === 'active' ? 'font-medium text-primary' : 'text-muted'
                }`}
              >
                {step.label}
              </span>
            </div>
            {!isLast ? (
              <div
                className={`ds-stepper-line mb-5 hidden sm:block ${lineComplete ? 'ds-stepper-line-complete' : ''}`}
              />
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
